import React, { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_tradeoff-platform/artifacts/g7lil3pc_03_dashboard.png", label: "Projects dashboard" },
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_tradeoff-platform/artifacts/mdpa12u9_04_takeoff.png", label: "Blueprint takeoff" },
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_tradeoff-platform/artifacts/4xqlm390_05_estimate.png", label: "Estimating matrix" },
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_tradeoff-platform/artifacts/ab03tmw2_06_billing.png", label: "Progress billing" },
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_tradeoff-platform/artifacts/oswep0ms_08_public_pay.png", label: "Application for Payment" },
];

const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const paused = useRef(false);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    const t = setInterval(() => { if (!paused.current) setIndex((i) => (i + 1) % SLIDES.length); }, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="relative"
      data-testid="hero-carousel"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl bg-white">
        <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-3">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        </div>
        <div className="relative w-full h-[340px] bg-slate-50 overflow-hidden">
          <AnimatePresence mode="popLayout">
            <motion.img
              key={index}
              src={SLIDES[index].src}
              alt={SLIDES[index].label}
              data-testid={`hero-slide-${index}`}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover object-top"
              loading="lazy"
            />
          </AnimatePresence>

          {/* Caption pill */}
          <div className="absolute top-3 left-3 z-10">
            <span className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-slate-700 text-xs font-semibold rounded-full px-3 py-1.5 shadow-sm border border-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> {SLIDES[index].label}
            </span>
          </div>

          {/* Arrows */}
          <button
            type="button"
            aria-label="Previous screenshot"
            data-testid="hero-carousel-prev"
            onClick={() => go(-1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white/85 hover:bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-700 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Next screenshot"
            data-testid="hero-carousel-next"
            onClick={() => go(1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white/85 hover:bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-700 transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Dots */}
      <div className="mt-4 flex items-center justify-center gap-2" data-testid="hero-carousel-dots">
        {SLIDES.map((s, i) => (
          <button
            key={s.label}
            type="button"
            aria-label={`Show ${s.label}`}
            data-testid={`hero-carousel-dot-${i}`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-7 bg-blue-600" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;
